import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import icon1 from "../../assets/images/home/features/Vector.png";
import icon2 from "../../assets/images/home/features/Vector-1.png";
import icon3 from "../../assets/images/home/features/Vector-2.png";
import icon4 from "../../assets/images/home/features/Vector-3.png";
import { useNavigate } from "react-router-dom";

const FeaturesSection = () => {
  const navigate = useNavigate();

  const cards = [
    {
      icon: icon1,
      title: "Digital Transformation",
      subtitle:
        "We help brands evolve through strategic design and smart technology.",
    },
    {
      icon: icon2,
      title: "Creative Experiences",
      subtitle:
        "Our mission is to craft engaging user journeys across digital platforms.",
    },
    {
      icon: icon3,
      title: "Scalable Innovation",
      subtitle:
        "We build flexible digital tools that grow with your business needs.",
    },
    {
      icon: icon4,
      title: "Tailored Tech Solutions",
      subtitle:
        "Empowering teams with custom systems for seamless digital progress.",
    },
  ];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hidden: {
      y: 40,
      opacity: 0,
      scale: 0.95,
    },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        delay: i * 0.15,
        duration: 0.6,
        ease: "easeOut",
      },
    }),
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: {
      scale: 0.9,
      opacity: 0,
    },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        delay: 0.8,
        duration: 0.4,
        ease: "easeOut",
      },
    },
    hover: {
      scale: 1.05,
      boxShadow: "0 10px 25px rgba(59, 130, 246, 0.3)",
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    tap: {
      scale: 0.98,
    },
  };

  return (
    <section
      ref={ref}
      className="w-full bg-gray-50 pt-16 px-4 sm:px-6 lg:px-12 overflow-hidden"
    >
      <div className="flex flex-col md:flex-row gap-10">
        {/* Left Section */}
        <motion.div
          className="md:w-1/2 flex flex-col justify-center gap-6 tracking-wide"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2
            style={{ lineHeight: "1.2" }}
            className="text-3xl md:text-4xl font-bold text-primaryBgColor"
            variants={itemVariants}
          >
            Redefining Investment Consulting with Precision
          </motion.h2>
          <motion.p
            className="text-secondaryTextColor text-base md:text-lg"
            variants={itemVariants}
          >
            TechSpear is a forward-thinking investment firm helping individuals
            and businesses grow their wealth through smart, technology-driven
            strategies. We combine data-driven insights with innovative
            solutions to deliver consistent results and long-term financial
            security.
          </motion.p>
          <motion.button
            onClick={() => navigate("/procure-funds")}
            className="w-full md:w-1/2 lg:w-1/3 mt-4 px-6 py-3 bg-primaryBtnColor text-white rounded-full tracking-wider font-semibold"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Start Consult <span className="text-2xl ml-2">◡̈</span>
          </motion.button>
        </motion.div>

        {/* Right Section */}
        <motion.div
          className="md:w-1/2 grid grid-cols-1 md:grid-cols-2 gap-6 tracking-wide"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {cards.map((card, index) => (
            <motion.div
              key={index}
              className="text-start"
              custom={index}
              variants={cardVariants}
              whileHover="hover"
            >
              <div className="mb-4 relative">
                <motion.img
                  src={card.icon}
                  alt={card.title}
                  className="w-12 h-12 object-contain relative z-10"
                  whileHover={{
                    scale: 1.2,
                    rotate: 5,
                    transition: { duration: 0.3 },
                  }}
                />
              </div>

              <motion.h3
                className="text-2xl font-semibold text-primaryBgColor mb-2"
                whileHover={{
                  color: "#2563eb",
                  transition: { duration: 0.3 },
                }}
              >
                {card.title}
              </motion.h3>
              <motion.p
                className="text-secondaryTextColor text-base"
                whileHover={{
                  color: "#374151",
                  transition: { duration: 0.3 },
                }}
              >
                {card.subtitle}
              </motion.p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
